import { Link } from 'react-router-dom';
import React from 'react';
import { Button } from '@/shared/ui/button';
import {
    NavigationMenu,
    NavigationMenuLink,
    NavigationMenuList,
    navigationMenuTriggerStyle,
} from '@/shared/ui/navigation-menu';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetClose,
    SheetDescription,
} from "@/shared/ui/sheet";
import { Menu } from 'lucide-react';
import { useSupabaseAuth } from '@/shared/hooks/useSupabaseAuth';
import { createClient } from '@/shared/api/supabase/client';
import { useNavigate } from 'react-router-dom';

export const Header = React.memo(() => {
    const { user, isLoading, isAdmin } = useSupabaseAuth();
    const router = useNavigate();

    const handleLogout = async () => {
        try {
            const supabase = createClient();
            const { error } = await supabase.auth.signOut();
            if (error) {
                console.error('ログアウトエラー:', error);
            } else {
                router('/login');
            }
        } catch (error) {
            console.error('ログアウト処理でエラーが発生しました:', error);
        }
    };

    const NavLinks = ({ isMobile = false }: { isMobile?: boolean }) => {
        // Base classes for navigation links, common for both desktop and mobile
        const navLinkBaseClasses = "px-3 py-2 rounded-md text-sm font-medium transition-colors";
        // Specific classes for desktop (using navigationMenuTriggerStyle for structure but customizing appearance)
        const desktopNavLinkClasses = `${navigationMenuTriggerStyle()} bg-transparent hover:bg-slate-700/50 hover:text-white focus:bg-slate-700/50 focus:text-white data-[active]:bg-slate-700/80 data-[state=open]:bg-slate-700/50 text-sm px-2 py-2 whitespace-nowrap`;
        // Specific classes for mobile (full width, text alignment)
        const mobileNavLinkClasses = `block w-full text-left ${navLinkBaseClasses} hover:bg-slate-700/50 hover:text-white`;

        if (isMobile) {
            // モバイル用: NavigationMenuLinkを使わず、通常のLinkのみ
            return (
                <>
                    <SheetClose asChild>
                        <Link to="/" className={mobileNavLinkClasses}>進行中案件</Link>
                    </SheetClose>
                    {isAdmin && (
                        <>
                            <SheetClose asChild>
                                <Link to="/projects" className={mobileNavLinkClasses}>案件一覧</Link>
                            </SheetClose>
                            {/* REMOVED: 材料管理は GenbaFlow から除外 */}
                            <SheetClose asChild>
                                <Link to="/masters" className={mobileNavLinkClasses}>マスタ管理</Link>
                            </SheetClose>
                            <SheetClose asChild>
                                <Link to="/users" className={mobileNavLinkClasses}>ユーザー管理</Link>
                            </SheetClose>
                            {/* REMOVED: データ分析は GenbaFlow から除外 */}
                        </>
                    )}
                </>
            );
        }
        // デスクトップ用: NavigationMenuLinkを直接使用（Linkでラップしない）
        return (
            <>
                <NavigationMenuLink asChild>
                    <Link to="/" className={desktopNavLinkClasses}>
                        進行中案件
                    </Link>
                </NavigationMenuLink>
                {isAdmin && (
                    <>
                        <NavigationMenuLink asChild>
                            <Link to="/projects" className={desktopNavLinkClasses}>案件一覧</Link>
                        </NavigationMenuLink>
                        {/* REMOVED: 材料管理は GenbaFlow から除外 */}
                        <NavigationMenuLink asChild>
                            <Link to="/masters" className={desktopNavLinkClasses}>マスタ管理</Link>
                        </NavigationMenuLink>
                        <NavigationMenuLink asChild>
                            <Link to="/users" className={desktopNavLinkClasses}>ユーザー管理</Link>
                        </NavigationMenuLink>
                        {/* REMOVED: データ分析は GenbaFlow から除外 */}
                    </>
                )}
            </>
        );
    };

    return (
        <header className="bg-slate-900 text-slate-100 shadow-lg sticky top-0 z-40">
            <div className="container mx-auto max-w-7xl px-4 py-4 flex justify-between items-center">
                <Link to="/" className="text-xl font-bold whitespace-nowrap hover:text-slate-300 transition-colors">
                    GenbaFlow
                </Link>

                {/* Desktop Navigation */}
                <NavigationMenu className="hidden lg:flex flex-1 justify-center mx-4">
                    <NavigationMenuList className="flex-wrap justify-center">
                        <NavLinks />
                    </NavigationMenuList>
                </NavigationMenu>

                {/* User Info / Logout Button (Desktop) */}
                <div className="hidden lg:flex items-center space-x-2 flex-shrink-0">
                    {isLoading ? (
                        <span className="text-sm text-slate-400">読み込み中...</span>
                    ) : user ? (
                        <>
                            <span className="text-xs font-medium truncate max-w-32">{user?.user_metadata?.name || user?.email}</span>
                            <Button variant="ghost" size="sm" onClick={handleLogout} className="hover:bg-slate-700/50 hover:text-white text-xs px-2">
                                ログアウト
                            </Button>
                        </>
                    ) : (
                        <Link to="/login">
                            <Button variant="outline" size="sm" className="border-slate-600 hover:bg-slate-700/50 hover:text-white hover:border-slate-500 text-xs px-2">ログイン</Button>
                        </Link>
                    )}
                </div>

                {/* Mobile/Tablet Navigation Button (Hamburger) */}
                <div className="lg:hidden">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="hover:bg-slate-700/50 focus:bg-slate-700/50">
                                <Menu className="h-6 w-6 text-slate-100" />
                                <span className="sr-only">メニューを開閉</span>
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="bg-slate-900 text-slate-100 border-slate-700 pt-10">
                            <SheetHeader className="mb-6">
                                <SheetTitle className="text-slate-100 text-center text-lg">ナビゲーション</SheetTitle>
                                <SheetDescription className="text-slate-300 text-center text-sm">
                                    アプリケーションのメインメニューです
                                </SheetDescription>
                            </SheetHeader>
                            <nav className="flex flex-col space-y-1">
                                <div className="flex flex-col space-y-1">
                                    <NavLinks isMobile={true} />
                                </div>

                                <hr className="my-6 border-slate-700" />

                                {isLoading ? (
                                    <span className="text-sm text-slate-400 px-3 py-2">読み込み中...</span>
                                ) : user ? (
                                    <div className="flex flex-col space-y-3 items-start px-2">
                                        <span className="text-sm font-medium px-1 py-2">{user?.user_metadata?.name || user?.email}</span>
                                        <SheetClose asChild>
                                            <Button variant="ghost" className="w-full justify-start hover:bg-slate-700/50 hover:text-white text-sm" onClick={handleLogout}>
                                                ログアウト
                                            </Button>
                                        </SheetClose>
                                    </div>
                                ) : (
                                    <SheetClose asChild>
                                        <Link to="/login" className='w-full px-2'>
                                            <Button variant="outline" className="w-full justify-start border-slate-600 hover:bg-slate-700/50 hover:text-white hover:border-slate-500 text-sm">
                                                ログイン
                                            </Button>
                                        </Link>
                                    </SheetClose>
                                )}
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
});

Header.displayName = 'Header'; 